import type {
  ImageAnalyses,
  LatestImageCalculus,
  LatestImageRestorativeAdjacentLucency,
  LatestImagesCaries,
} from '../../../../../@types/latestImagesV2';
import type {
  CALCULUS,
  CARIES,
  PERIAPICAL_RADIOLUCENCY,
  RESTORATIVE_ADJACENT_LUCENCY,
} from '../../../../../constants';
import {
  red400,
  yellow500,
  yellowGreen100,
  yellowOrange30085,
} from '../../../../../theme/videa-style-tokens';
import type { SliderRange } from '../../../../annotator/types';

export type TIsWithinSensitivityRange = (
  type:
    | typeof CARIES
    | typeof RESTORATIVE_ADJACENT_LUCENCY
    | typeof PERIAPICAL_RADIOLUCENCY
    | typeof CALCULUS,
  confidenceScore?: number | null,
  bin?: number | null,
) => boolean;

// Duplicate of client/src/modules/annotator/annotation-utils/incipient.ts:L62
export const isWithinSensitivityRange = (
  finding: LatestImagesCaries,
  image: ImageAnalyses,
  desiredVisibleBoxSizeRange: SliderRange = [0, 0],
): boolean => {
  if (!finding) {
    return false;
  }
  let { heightPx, widthPx } = image;
  heightPx = heightPx ?? 1440;
  widthPx = widthPx ?? 1920;

  const xMax = finding.bounds.coordinates[0][0][0];
  const xMin = finding.bounds.coordinates[0][0][1];
  const yMax = finding.bounds.coordinates[0][1][0];
  const yMin = finding.bounds.coordinates[0][1][1];

  const findingWidth = xMax - xMin;
  const findingHeight = yMax - yMin;

  const findingSizePercentageRelativeToImageSize =
    (100 * (findingWidth * findingHeight)) / (widthPx * heightPx);

  const [minimumBoxSize, maximumBoxSize] = desiredVisibleBoxSizeRange;
  return (
    findingSizePercentageRelativeToImageSize > minimumBoxSize &&
    findingSizePercentageRelativeToImageSize < maximumBoxSize
  );
};

export const getCariesSubTypedFindings = (carie: LatestImagesCaries) => {
  if (carie?.depth === 'into_enamel') {
    return 'incipient';
  }

  return 'caries';
};

export const getCariesFindings = (input: {
  caries: LatestImagesCaries[];
  showIncipientCariesRed?: boolean;
}) => {
  const { caries, showIncipientCariesRed } = input;
  return caries
    .filter((carie) => {
      return !!carie.bounds.coordinates;
    })
    .map((carie) => {
      const coordinates = carie.bounds.coordinates[0] as number[][];
      const minX = Math.min(...coordinates.map((coord) => coord[0] as number));
      const maxX = Math.max(...coordinates.map((coord) => coord[0] as number));
      const minY = Math.min(...coordinates.map((coord) => coord[1] as number));
      const maxY = Math.max(...coordinates.map((coord) => coord[1] as number));

      const type = getCariesSubTypedFindings(carie);

      return {
        x: minX,
        y: minY,
        width: maxX - minX,
        height: maxY - minY,
        id: carie.id,
        styles: {
          strokeColor:
            type === 'incipient' && !showIncipientCariesRed
              ? yellow500
              : red400,
          strokeWidth: 2,
        },
      };
    });
};

export const getCalculusFindings = (input: {
  calculusFindings: LatestImageCalculus[];
}) => {
  const { calculusFindings } = input;
  return calculusFindings
    .filter((calculus) => calculus.bounds.coordinates)
    .map((calculus) => {
      const coordinates = calculus.bounds.coordinates[0] as number[][];
      const minX = Math.min(...coordinates.map((coord) => coord[0] as number));
      const maxX = Math.max(...coordinates.map((coord) => coord[0] as number));
      const minY = Math.min(...coordinates.map((coord) => coord[1] as number));
      const maxY = Math.max(...coordinates.map((coord) => coord[1] as number));

      return {
        x: minX,
        y: minY,
        width: maxX - minX,
        height: maxY - minY,
        id: calculus.id,
        styles: {
          strokeColor: yellowOrange30085,
          strokeWidth: 2,
          strokeDashArray: [3, 2],
        },
      };
    });
};

export const getRestorativeAdjacentLucencyFindings = (input: {
  restorativeAdjacentLucency: LatestImageRestorativeAdjacentLucency[];
}) => {
  const { restorativeAdjacentLucency } = input;
  return restorativeAdjacentLucency
    .filter((lucency) => {
      return !!lucency.bounds.coordinates;
    })
    .map((lucency) => {
      const coordinates = lucency.bounds.coordinates[0] as number[][];
      const minX = Math.min(...coordinates.map((coord) => coord[0] as number));
      const maxX = Math.max(...coordinates.map((coord) => coord[0] as number));
      const minY = Math.min(...coordinates.map((coord) => coord[1] as number));
      const maxY = Math.max(...coordinates.map((coord) => coord[1] as number));

      return {
        x: minX,
        y: minY,
        width: maxX - minX,
        height: maxY - minY,
        id: lucency.id,
        styles: {
          strokeColor: yellowGreen100,
          strokeWidth: 2,
          strokeDashArray: [2, 2],
        },
      };
    });
};
